<script setup>
import { ref, computed, onMounted } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import WorkflowFieldEditor from './WorkflowFieldEditor.vue';
import WorkflowFieldTemplatesAPI from 'dashboard/api/workflowFieldTemplates';
import CustomAttributeBundlesAPI from 'dashboard/api/customAttributeBundles';
import {
  useStore,
  useStoreGetters,
} from 'dashboard/composables/store';

const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
});

const emit = defineEmits(['update:modelValue']);
const store = useStore();
const getters = useStoreGetters();

// Capture sources for auto-fill dropdown
const captureSources = ref([]);
const customAttributeBundles = ref([]);
const ticketAttributeDefinitions = computed(
  () => getters['attributes/getTicketAttributes'].value || []
);

const availableBundles = computed(() =>
  customAttributeBundles.value.filter(bundle =>
    bundle.fields.every(field =>
      ticketAttributeDefinitions.value.some(
        attr => attr.attributeKey === field.attribute_key
      )
    )
  )
);

onMounted(async () => {
  try {
    const response = await WorkflowFieldTemplatesAPI.get();
    captureSources.value = response.data.capture_sources || [];
  } catch {
    captureSources.value = [];
  }
  store.dispatch('attributes/get');

  try {
    const response = await CustomAttributeBundlesAPI.get({
      attribute_model: 'ticket_attribute',
    });
    customAttributeBundles.value = response.data || [];
  } catch {
    customAttributeBundles.value = [];
  }
});

const updateConfig = (key, value) => {
  emit('update:modelValue', { ...props.modelValue, [key]: value });
};

const config = computed({
  get: () => props.modelValue || {},
  set: val => emit('update:modelValue', val),
});

const initialResponse = computed({
  get: () => config.value.initial_response || '',
  set: val => updateConfig('initial_response', val),
});

const fields = computed({
  get: () => config.value.fields || [],
  set: val => updateConfig('fields', val),
});

const escalation = computed({
  get: () =>
    config.value.escalation || { timing: 'after_required_fields', rules: [] },
  set: val => updateConfig('escalation', val),
});

const onComplete = computed({
  get: () =>
    config.value.on_complete || {
      action: 'create_service_ticket',
      title_template: '',
    },
  set: val => updateConfig('on_complete', val),
});

const DEDUP_STRATEGY_VALUES = [
  '',
  'none',
  'contract',
  'contract_and_address',
  'address',
  'contract_and_explotacion',
];

const patchDedup = partial => {
  const next = { ...(props.modelValue || {}) };
  const dedup = { ...(next.dedup || {}), ...partial };
  Object.keys(dedup).forEach(k => {
    const v = dedup[k];
    if (v === undefined || v === null || v === '') {
      delete dedup[k];
    }
  });
  if (Object.keys(dedup).length === 0) {
    delete next.dedup;
  } else {
    next.dedup = dedup;
  }
  emit('update:modelValue', next);
};

const dedupStrategySelect = computed({
  get: () => {
    const s = config.value.dedup?.strategy;
    return s && DEDUP_STRATEGY_VALUES.includes(s) ? s : '';
  },
  set: val => {
    if (!val) {
      patchDedup({ strategy: undefined });
    } else {
      patchDedup({ strategy: val });
    }
  },
});

const dedupCooldownHours = computed({
  get: () => {
    const v = config.value.dedup?.cooldown_hours;
    if (v === undefined || v === null || v === '') return '';
    return String(v);
  },
  set: val => {
    const n = parseInt(String(val).trim(), 10);
    if (Number.isNaN(n) || n <= 0) {
      patchDedup({ cooldown_hours: undefined });
    } else {
      patchDedup({ cooldown_hours: n });
    }
  },
});

const onDedupStrategyChange = e => {
  dedupStrategySelect.value = e.target.value;
};

const onDedupCooldownInput = e => {
  dedupCooldownHours.value = e.target.value;
};

// Field management - inline editor (no nested modal)
const showFieldEditor = ref(false);
const editingFieldIndex = ref(null);
const editingField = ref(null);

const defaultField = () => ({
  key: '',
  label: '',
  type: '',
  priority: 'required',
  order: fields.value.length + 1,
  prompt: '',
  examples: [],
  skip_response: null,
});

const openAddField = () => {
  editingFieldIndex.value = null;
  editingField.value = defaultField();
  showFieldEditor.value = true;
};

const openEditField = (field, index) => {
  editingFieldIndex.value = index;
  editingField.value = { ...field, examples: [...(field.examples || [])] };
  showFieldEditor.value = true;
};

const saveField = () => {
  const selectedKey = editingField.value?.key;
  const hasAttribute = ticketAttributeDefinitions.value.some(
    attr => attr.attributeKey === selectedKey
  );
  if (!hasAttribute) return;

  const newFields = [...fields.value];
  if (editingFieldIndex.value !== null) {
    newFields[editingFieldIndex.value] = editingField.value;
  } else {
    newFields.push(editingField.value);
  }
  fields.value = newFields;
  showFieldEditor.value = false;
  editingField.value = null;
};

const cancelFieldEdit = () => {
  showFieldEditor.value = false;
  editingField.value = null;
};

const removeField = index => {
  const newFields = fields.value.filter((_, i) => i !== index);
  fields.value = newFields;
};

const addBundle = bundle => {
  const existingKeys = new Set(fields.value.map(field => field.key));
  const nextFields = [...fields.value];

  bundle.fields.forEach(bundleField => {
    const fieldKey = bundleField.attribute_key;
    if (existingKeys.has(fieldKey)) return;

    const attr = ticketAttributeDefinitions.value.find(
      item => item.attributeKey === fieldKey
    );
    if (!attr) return;

    nextFields.push({
      key: attr.attributeKey,
      label: attr.attributeDisplayName,
      type: attr.attributeDisplayType,
      priority: bundleField.required === false ? 'optional' : 'required',
      order: bundleField.order || nextFields.length + 1,
      prompt: '',
      examples: [],
      skip_response: null,
    });
    existingKeys.add(fieldKey);
  });

  fields.value = nextFields;
};

// Escalation rules
const addEscalationRule = () => {
  const newRules = [
    ...(escalation.value.rules || []),
    { if: { field: '', equals: '' }, then: 'escalate' },
  ];
  escalation.value = { ...escalation.value, rules: newRules };
};

const removeEscalationRule = index => {
  const newRules = escalation.value.rules.filter((_, i) => i !== index);
  escalation.value = { ...escalation.value, rules: newRules };
};

const updateEscalationTiming = timing => {
  escalation.value = { ...escalation.value, timing };
};

const updateEscalationRule = (index, rule) => {
  const newRules = [...escalation.value.rules];
  newRules[index] = rule;
  escalation.value = { ...escalation.value, rules: newRules };
};

// Priority display helpers
const priorityLabels = {
  required: { label: 'Obligatorio', color: 'bg-r-100 text-r-800' },
  recommended: { label: 'Recomendado', color: 'bg-y-100 text-y-800' },
  optional: { label: 'Opcional', color: 'bg-n-solid-3 text-n-slate-11' },
};

const timingOptions = [
  { value: 'immediate', label: 'Inmediato' },
  { value: 'after_required_fields', label: 'Después de campos obligatorios' },
  { value: 'after_all_fields', label: 'Después de todos los campos' },
];
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text, prettier/prettier -->
  <div class="flex flex-col gap-4">
    <!-- Field Editor (inline, replaces main content when editing) -->
    <template v-if="showFieldEditor">
      <WorkflowFieldEditor
        :field="editingField"
        :ticket-attributes="ticketAttributeDefinitions"
        :capture-sources="captureSources"
        @update:field="editingField = $event"
        @save="saveField"
        @cancel="cancelFieldEdit"
      />
    </template>

    <!-- Main Content (hidden when editing field) -->
    <template v-else>
      <!-- Initial Response -->
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('WORKFLOW_CONFIG.INITIAL_RESPONSE') }}
        </label>
        <textarea
          v-model="initialResponse"
          rows="2"
          class="w-full px-3 py-2 rounded-lg bg-n-alpha-1 border border-n-weak focus:border-woot-500 focus:outline-none text-sm"
          :placeholder="$t('WORKFLOW_CONFIG.INITIAL_RESPONSE_PLACEHOLDER')"
        />
      </div>

      <!-- Fields Section -->
      <div class="flex flex-col gap-2">
        <div class="flex items-center justify-between">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('WORKFLOW_CONFIG.FIELDS') }}
          </label>
          <Button
            type="button"
            icon="i-lucide-plus"
            slate
            xs
            faded
            :label="$t('WORKFLOW_CONFIG.ADD_FIELD')"
            @click="openAddField"
          />
        </div>
        <div v-if="availableBundles.length" class="flex flex-wrap gap-2">
          <Button
            v-for="bundle in availableBundles"
            :key="bundle.bundle_key"
            type="button"
            icon="i-lucide-package-plus"
            slate
            xs
            faded
            :label="bundle.bundle_name"
            @click="addBundle(bundle)"
          />
        </div>

        <div v-if="fields.length" class="flex flex-col gap-1">
          <div
            v-for="(field, index) in fields"
            :key="index"
            class="flex items-center justify-between p-2 rounded-lg bg-n-solid-2 border border-n-weak"
          >
            <div class="flex items-center gap-2">
              <span class="text-xs text-n-slate-10 w-5">{{ field.order }}</span>
              <span class="font-mono text-xs text-n-slate-11">
                {{ field.key }}
              </span>
              <span class="text-sm text-n-slate-12">{{ field.label }}</span>
              <span
                class="text-xs px-1.5 py-0.5 rounded"
                :class="priorityLabels[field.priority]?.color"
              >
                {{ priorityLabels[field.priority]?.label }}
              </span>
              <span class="text-xs text-n-slate-10">{{ field.type }}</span>
            </div>
            <div class="flex items-center gap-1">
              <Button
                type="button"
                icon="i-lucide-pen"
                slate
                xs
                faded
                @click="openEditField(field, index)"
              />
              <Button
                type="button"
                icon="i-lucide-trash-2"
                ruby
                xs
                faded
                @click="removeField(index)"
              />
            </div>
          </div>
        </div>
        <div v-else class="text-sm text-n-slate-10 p-2">
          {{ $t('WORKFLOW_CONFIG.NO_FIELDS') }}
        </div>
      </div>

      <!-- Escalation Section -->
      <div class="flex flex-col gap-2">
        <div class="flex items-center justify-between">
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('WORKFLOW_CONFIG.ESCALATION') }}
          </label>
          <Button
            type="button"
            icon="i-lucide-plus"
            slate
            xs
            faded
            :label="$t('WORKFLOW_CONFIG.ADD_RULE')"
            @click="addEscalationRule"
          />
        </div>

        <div class="flex items-center gap-2">
          <span class="text-sm text-n-slate-11">
            {{ $t('WORKFLOW_CONFIG.TIMING') }}
          </span>
          <select
            :value="escalation.timing"
            class="px-2 py-1 rounded-lg bg-n-alpha-1 border border-n-weak text-sm"
            @change="updateEscalationTiming($event.target.value)"
          >
            <option
              v-for="opt in timingOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>

        <div v-if="escalation.rules?.length" class="flex flex-col gap-1">
          <div
            v-for="(rule, index) in escalation.rules"
            :key="index"
            class="flex items-center gap-2 p-2 rounded-lg bg-n-solid-2 border border-n-weak"
          >
            <span class="text-xs text-n-slate-11">IF</span>
            <input
              :value="rule.if?.field"
              type="text"
              class="w-24 px-2 py-1 rounded bg-n-alpha-1 border border-n-weak text-xs"
              placeholder="field"
              @input="
                updateEscalationRule(index, {
                  ...rule,
                  if: { ...rule.if, field: $event.target.value },
                })
              "
            />
            <span class="text-xs text-n-slate-11">=</span>
            <input
              :value="rule.if?.equals"
              type="text"
              class="w-24 px-2 py-1 rounded bg-n-alpha-1 border border-n-weak text-xs"
              placeholder="value"
              @input="
                updateEscalationRule(index, {
                  ...rule,
                  if: { ...rule.if, equals: $event.target.value },
                })
              "
            />
            <span class="text-xs text-n-slate-11">THEN escalate</span>
            <Button
              type="button"
              icon="i-lucide-trash-2"
              ruby
              xs
              faded
              class="ml-auto"
              @click="removeEscalationRule(index)"
            />
          </div>
        </div>
      </div>

      <!-- Duplicate open tickets -->
      <div class="flex flex-col gap-2 p-3 rounded-lg bg-n-solid-2 border border-n-weak">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('WORKFLOW_CONFIG.DEDUP_TITLE') }}
        </label>
        <p class="text-xs text-n-slate-10">
          {{ $t('WORKFLOW_CONFIG.DEDUP_HELP') }}
        </p>
        <div class="flex flex-col gap-1">
          <span class="text-xs text-n-slate-11">{{
            $t('WORKFLOW_CONFIG.DEDUP_STRATEGY')
          }}</span>
          <select
            :value="dedupStrategySelect"
            class="w-full px-2 py-1 rounded-lg bg-n-alpha-1 border border-n-weak text-sm"
            @change="onDedupStrategyChange"
          >
            <option value="">
              {{ $t('WORKFLOW_CONFIG.DEDUP_STRATEGY_DERIVED') }}
            </option>
            <option value="none">{{ $t('WORKFLOW_CONFIG.DEDUP_OPT_NONE') }}</option>
            <option value="contract">
              {{ $t('WORKFLOW_CONFIG.DEDUP_OPT_CONTRACT') }}
            </option>
            <option value="contract_and_address">
              {{ $t('WORKFLOW_CONFIG.DEDUP_OPT_CONTRACT_AND_ADDRESS') }}
            </option>
            <option value="address">
              {{ $t('WORKFLOW_CONFIG.DEDUP_OPT_ADDRESS') }}
            </option>
            <option value="contract_and_explotacion">
              {{ $t('WORKFLOW_CONFIG.DEDUP_OPT_CONTRACT_AND_EXPLOTACION') }}
            </option>
          </select>
        </div>
        <div class="flex flex-col gap-1">
          <span class="text-xs text-n-slate-11">{{
            $t('WORKFLOW_CONFIG.DEDUP_COOLDOWN_HOURS')
          }}</span>
          <input
            :value="dedupCooldownHours"
            type="number"
            min="1"
            class="w-full px-2 py-1 rounded-lg bg-n-alpha-1 border border-n-weak text-sm"
            :placeholder="$t('WORKFLOW_CONFIG.DEDUP_COOLDOWN_PLACEHOLDER')"
            @input="onDedupCooldownInput"
          />
          <span class="text-xs text-n-slate-10">{{
            $t('WORKFLOW_CONFIG.DEDUP_COOLDOWN_HELP')
          }}</span>
        </div>
      </div>

      <!-- On Complete Section -->
      <div class="flex flex-col gap-2">
        <label class="text-sm font-medium text-n-slate-12">
          {{ $t('WORKFLOW_CONFIG.ON_COMPLETE') }}
        </label>
        <div class="flex flex-col gap-2 p-3 rounded-lg bg-n-solid-2 border border-n-weak">
          <div class="flex items-center gap-2">
            <span class="text-sm text-n-slate-11 w-16">{{ $t('WORKFLOW_CONFIG.ACTION') }}:</span>
            <select
              :value="onComplete.action"
              class="flex-1 px-2 py-1 rounded-lg bg-n-alpha-1 border border-n-weak text-sm"
              @change="onComplete = { ...onComplete, action: $event.target.value }"
            >
              <option value="create_service_ticket">Crear ticket de servicio</option>
            </select>
          </div>
          <div class="flex items-center gap-2">
            <span class="text-sm text-n-slate-11 w-16">{{ $t('WORKFLOW_CONFIG.TITLE_TEMPLATE') }}:</span>
            <input
              :value="onComplete.title_template"
              type="text"
              class="flex-1 px-2 py-1 rounded-lg bg-n-alpha-1 border border-n-weak text-sm"
              placeholder="Título del ticket - {campo}"
              @input="onComplete = { ...onComplete, title_template: $event.target.value }"
            />
          </div>
        </div>
      </div>
    </template>
  </div>
</template>
